'use client';

import { useState } from 'react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Separator } from '@/components/ui/layout/separator';
import { Button } from '@/components/ui/forms/button';
import { ResumeShareControls } from './ResumeShareControls';
import { ResumeExportControls } from './ResumeExportControls';
import type { ResumeShare } from '@/lib/resume/types';

interface ResumeShareExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeId: string;
  resumeName: string;
  initialShare?: ResumeShare;
  onShareChange?: (share: ResumeShare) => void;
}

/**
 * Sharing and exporting in one window.
 *
 * Sharing leads because it is the intended path — sending a live link rather
 * than a file — with downloading kept a scroll away for when a file is what
 * the recipient actually wants.
 */
export function ResumeShareExportDialog({
  open,
  onOpenChange,
  resumeId,
  resumeName,
  initialShare,
  onShareChange,
}: ResumeShareExportDialogProps) {
  const [shareBusy, setShareBusy] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeading
          title="Share &amp; export"
          description={<>Send a link to &quot;{resumeName}&quot;, or download a copy.</>}
        />

        <div className="space-y-4 py-2">
          <ResumeShareControls
            active={open}
            resumeId={resumeId}
            initialShare={initialShare}
            onShareChange={onShareChange}
            onBusyChange={setShareBusy}
          />

          <Separator />

          <ResumeExportControls resumeId={resumeId} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={shareBusy}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
